var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from "react";
import { DayPicker } from "react-day-picker";
import FocusLock from "react-focus-lock";
import { format, isValid, isExists } from "date-fns";
import { useOutsideClick } from "../../internal-hooks/useOutsideClick";
import { useAriaHidden } from "../../internal-hooks/useAriaHidden";
import { ContainerDatePickerStyle, InputDatePickerStyle } from "./styled";
import { DateRangeIcon } from "../Icons/DateRangeIcon";
import { CloseIcon } from "../Icons/CloseIcon";
import { addDay } from "../RangeDatePicker/utils";
import { ITEM_TEXT_FORMAT, MonthMenu } from "../RangeDatePicker/MonthMenu";
import { Popper } from "../Popper";
var MAX_DATE = new Date("9999-12-01");
var FUTURE_DATE_VALIDATION = "Cannot use future date";
var UNAVAILABLE_DATE_VALIDATION = "Please enter a valid date";
var DatePicker = function (_a) {
    var onChange = _a.onChange, _b = _a.defaultDate, defaultDate = _b === void 0 ? undefined : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.disableFutureDays, disableFutureDays = _d === void 0 ? false : _d, id = _a.id, className = _a.className, style = _a.style, label = _a.label, _e = _a.placeholder, placeholder = _e === void 0 ? "" : _e, _f = _a.required, required = _f === void 0 ? false : _f, onError = _a.onError, _g = _a.value, value = _g === void 0 ? null : _g;
    var TODAY = React.useMemo(function () { return new Date(); }, []);
    var TOMORROW = React.useMemo(function () { return addDay(TODAY); }, [TODAY]);
    var _h = __read(React.useState(null), 2), popperTarget = _h[0], setPopperTarget = _h[1];
    var _j = __read(React.useState(null), 2), calendarInput = _j[0], setCalendarInput = _j[1];
    var _k = __read(React.useState(null), 2), popperContainer = _k[0], setPopperContainer = _k[1];
    var _l = __read(React.useState(false), 2), isOpen = _l[0], setIsOpen = _l[1];
    // If the user is typing a date, the current text content of the date.
    // If the date is entered, this is null.
    var _m = __read(React.useState(null), 2), dateInput = _m[0], setDateInput = _m[1];
    var _o = __read(React.useState(TODAY), 2), currentMonth = _o[0], setCurrentMonth = _o[1];
    var _p = __read(React.useState((function () {
        if (!defaultDate)
            return null;
        if (!disableFutureDays)
            return defaultDate;
        if (defaultDate.getTime() <= TODAY.getTime()) {
            return defaultDate;
        }
        return null;
    })()), 2), uncontrolledValue = _p[0], setUncontrolledValue = _p[1];
    var currentValue = React.useMemo(function () {
        if (dateInput) {
            var _a = __read(dateInput.split("/"), 3), monthStr = _a[0], dayStr = _a[1], yearStr = _a[2];
            var date = componentToDate(+yearStr, +monthStr, +dayStr);
            return date;
        }
        return value !== null && value !== void 0 ? value : uncontrolledValue;
    }, [value, dateInput, uncontrolledValue]);
    var error = React.useMemo(function () { return validateDate(currentValue, required, disableFutureDays); }, [currentValue, required, disableFutureDays]);
    // It is vital that we do not call onError on response to onError changing.
    // This is because, if our consumer neglects to use useCallback on our onError handler,
    // and if that handler causes a re-render of our consumer, we will get stuck in an infinite loop
    // To prevent this, we use a ref to store the handler, so that we can call the most recent version
    // without having to include it as a dependency to our useEffect.
    var onErrorHandlerRef = React.useRef(onError);
    onErrorHandlerRef.current = onError;
    React.useEffect(function () {
        var handler = onErrorHandlerRef.current;
        if (handler) {
            handler(error != null);
        }
    }, [error]);
    var onOpen = React.useCallback(function () {
        setIsOpen(true);
        setCurrentMonth(currentValue !== null && currentValue !== void 0 ? currentValue : TODAY);
    }, [currentValue]);
    var handlerDayChange = React.useCallback(function (selectedDate) {
        setDateInput(null);
        onChange(selectedDate !== null && selectedDate !== void 0 ? selectedDate : null);
        setUncontrolledValue(selectedDate !== null && selectedDate !== void 0 ? selectedDate : null);
        setIsOpen(false);
    }, [onChange]);
    var onClear = React.useCallback(function () {
        setIsOpen(false);
        handlerDayChange(null);
    }, [handlerDayChange]);
    // render the weekdays after the component's render
    React.useEffect(function () {
        // generates the weekdays caption
        if (isOpen) {
            var elements = document.getElementsByClassName("rdp-head_cell");
            for (var i = 0; i < elements.length; i++) {
                var doc = document.createElement("h4");
                doc.textContent = getWeekCaption(i);
                doc.className = "calendar-weekday";
                elements[i].appendChild(doc);
            }
        }
    }, [isOpen]);
    var datePickerStatus = "";
    if (disabled) {
        datePickerStatus = "disabled";
    }
    else if (error != null) {
        datePickerStatus = "wrong";
    }
    else if (isOpen) {
        datePickerStatus = "clicked";
    }
    var handleInputCommit = React.useCallback(function () {
        var value = dateInput;
        setDateInput(null);
        setIsOpen(false);
        if (value == null || value === "") {
            onChange(null);
            setUncontrolledValue(null);
            return;
        }
        var _a = __read(value.split("/"), 3), monthStr = _a[0], dayStr = _a[1], yearStr = _a[2];
        var date = componentToDate(+yearStr, +monthStr, +dayStr);
        if (validateDate(date, required, disableFutureDays) !== null) {
            return;
        }
        onChange(date);
        setUncontrolledValue(date);
    }, [dateInput, onChange, required, disableFutureDays]);
    var handleInputFocus = React.useCallback(function () {
        if (!isOpen) {
            onOpen();
            setDateInput(currentValue ? format(currentValue, "P") : "");
        }
    }, [currentValue, isOpen]);
    var handleInputKeyPress = React.useCallback(function (e) {
        if (dateInput == null) {
            // Not editing.
            return;
        }
        if (e.key === "Enter") {
            handleInputCommit();
        }
    }, [handleInputCommit, dateInput]);
    var handleInputChange = React.useCallback(function (e) {
        var value = e.target.value;
        setDateInput(value);
        if (value == "") {
            return;
        }
        var _a = __read(value.split("/"), 3), monthStr = _a[0], dayStr = _a[1], yearStr = _a[2];
        var date = componentToDate(+yearStr, +monthStr, +dayStr);
        setCurrentMonth(isValid(date) ? date : new Date());
    }, []);
    var formatCaption = function (date) {
        return (React.createElement(React.Fragment, null,
            format(date, ITEM_TEXT_FORMAT),
            React.createElement(MonthMenu, { date: currentMonth, handleMonthChange: setCurrentMonth })));
    };
    var targetElements = React.useMemo(function () { return [popperContainer, calendarInput]; }, [
        popperContainer,
        calendarInput
    ]);
    var shardsArray = React.useMemo(function () { return (calendarInput ? [calendarInput] : []); }, [calendarInput]);
    // Close the menu if the user goes somewhere else.
    var handleOutsideClick = React.useCallback(function () {
        handleInputCommit();
    }, [handleInputCommit]);
    useOutsideClick(targetElements, handleOutsideClick, isOpen);
    // Lock screen readers into our field when the menu is open.
    useAriaHidden(isOpen, targetElements);
    var disabledRange = React.useMemo(function () { return (disableFutureDays ? { from: TOMORROW, to: MAX_DATE } : undefined); }, [disableFutureDays]);
    return (React.createElement(React.Fragment, null,
        React.createElement(InputDatePickerStyle, { id: id, className: className, style: style },
            label ? React.createElement("label", { className: "calendar-label" }, label) : null,
            React.createElement("div", { ref: setPopperTarget, className: "calendar-input ".concat(datePickerStatus) },
                React.createElement("input", { ref: setCalendarInput, className: "calendar-text ".concat(datePickerStatus), type: "text", value: dateInput !== null && dateInput !== void 0 ? dateInput : (currentValue ? format(currentValue, "P") : ""), onChange: handleInputChange, placeholder: placeholder || "MM/DD/YYYY", onFocus: handleInputFocus, onKeyPress: handleInputKeyPress }),
                React.createElement(DateRangeIcon, { className: currentValue === null ? "icon" : "hidden", onClick: onOpen }),
                React.createElement(CloseIcon, { className: currentValue !== null ? "icon" : "hidden", onClick: onClear })),
            error != null ? React.createElement("div", { className: "error-message" }, error) : null),
        React.createElement(Popper, { isOpen: isOpen, placement: "bottom-end", anchorEl: popperTarget, ref: setPopperContainer },
            React.createElement(FocusLock, { autoFocus: false, shards: shardsArray },
                React.createElement(ContainerDatePickerStyle, null,
                    React.createElement(DayPicker, { mode: "single", className: "calendar-container", onSelect: handlerDayChange, selected: currentValue !== null && currentValue !== void 0 ? currentValue : undefined, showOutsideDays: true, fixedWeeks: true, onMonthChange: setCurrentMonth, month: currentMonth, disabled: disabledRange, formatters: { formatCaption: formatCaption } }))))));
};
export default DatePicker;
function componentToDate(year, month, day) {
    if (!isExists(year, month - 1, day)) {
        return new Date("invalid");
    }
    var date = new Date();
    date.setFullYear(year);
    date.setMonth(month - 1);
    date.setDate(day);
    return date;
}
function getWeekCaption(day) {
    var dayOfWeek = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    day = day > 6 ? day - 7 : day;
    return dayOfWeek[day];
}
function validateDate(date, required, disableFutureDays) {
    if (!date) {
        return required ? UNAVAILABLE_DATE_VALIDATION : null;
    }
    if (!isValid(date) || date.getFullYear() < 1901) {
        return UNAVAILABLE_DATE_VALIDATION;
    }
    if (disableFutureDays && date.getTime() > new Date().getTime()) {
        return FUTURE_DATE_VALIDATION;
    }
    return null;
}
//# sourceMappingURL=DatePicker.js.map